import { gql } from '@graphql/__generated__';
import { type PropertyDetailsFragment } from '@graphql/__generated__/graphql';
import { propertyDetailsConditionalFields } from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormDefinition';
import type { PropertyTypeLight } from '@modules/_shared/components/property-form/propertyFormQueries';
import {
  PropertyDataList,
  type TFieldsDefinition,
} from '@modules/_shared/components/PropertyDataList';
import { getRatingLabel } from '@modules/_shared/utils/formatting';
import {
  extractPropertyOrientations,
  getEnergyClassValues,
  getOrientationTranslation,
  getPropertyCoolingLabel,
  getPropertyHeatingDistributionLabel,
  getPropertyHeatingLabel,
  getPropertySides,
} from '@modules/_shared/utils/propertyTemplateFunctions';
import { type Translate, useLocale } from '@shared/hooks/locale';

export type TPropertyDetails = PropertyDetailsFragment & {
  __main_type?: string | null;
  __property_type?: PropertyTypeLight;
};

export const PROPERTY_DETAILS_FRAGMENT = gql(/* GraphQL */ `
  fragment PropertyDetails on properties {
    residential_surface
    commercial_surface
    living_surface
    built_surface
    usable_surface
    gross_floor_surface
    balcony_surface
    terrace_surface
    garden_surface
    land_surface
    basement_surface
    weighted_floor_surface
    building_volume
    number_of_residential_units
    number_of_commercial_units
    number_of_rooms
    number_of_bedrooms
    number_of_bathrooms
    number_of_toilets
    number_of_floors
    floor_of_flat
    number_of_lifts
    side
    is_oriented_south
    is_oriented_west
    is_oriented_north
    is_oriented_east
    number_of_indoor_parkings
    number_of_outdoor_parkings
    heating
    heating_distribution
    cooling
    energy_consumption_class
    gas_emissions_class
    construction_year
    renovation_year
    building_standard
    building_condition
    property_type {
      id
      name
      label
      main_type
    }
  }
`);

interface PropertyDetailsProps {
  property: TPropertyDetails | null;
}

type PropertyDetailsData = Omit<
  PropertyDetailsFragment,
  | 'is_oriented_north'
  | 'is_oriented_south'
  | 'is_oriented_east'
  | 'is_oriented_west'
> & {
  orientation: 'N' | 'S' | 'E' | 'W' | 'NE' | 'NW' | 'SE' | 'SW';
};

const getPropertyDetailsFieldsDefinition = (
  t: Translate,
): TFieldsDefinition<keyof PropertyDetailsData> => {
  const sqrMetersSuffix = (value: string | number | null | undefined) =>
    value != null ? `${value} m²` : null;

  const fields: TFieldsDefinition<keyof PropertyDetailsData> = {
    // PROPERTY TYPE
    property_type: {
      label: t('Property type'),
      name: 'property_type',
      renderFn: value => value?.label,
    },
    // SURFACES
    residential_surface: {
      label: t('Residential surface'),
      name: 'residential_surface',
      renderFn: sqrMetersSuffix,
    },
    commercial_surface: {
      label: t('Commercial surface'),
      name: 'commercial_surface',
      renderFn: sqrMetersSuffix,
    },
    living_surface: {
      label: t('Living surface'),
      name: 'living_surface',
      renderFn: sqrMetersSuffix,
    },
    built_surface: {
      label: t('Built surface'),
      name: 'built_surface',
      renderFn: sqrMetersSuffix,
    },
    usable_surface: {
      label: t('Usable surface'),
      name: 'usable_surface',
      renderFn: sqrMetersSuffix,
    },
    gross_floor_surface: {
      label: t('Gross floor surface'),
      name: 'gross_floor_surface',
      renderFn: sqrMetersSuffix,
    },
    balcony_surface: {
      label: t('Balcony surface'),
      name: 'balcony_surface',
      renderFn: sqrMetersSuffix,
    },
    terrace_surface: {
      label: t('Terrace surface'),
      name: 'terrace_surface',
      renderFn: sqrMetersSuffix,
    },
    garden_surface: {
      label: t('Garden surface'),
      name: 'garden_surface',
      renderFn: sqrMetersSuffix,
    },
    land_surface: {
      label: t('Land surface'),
      name: 'land_surface',
      renderFn: sqrMetersSuffix,
    },
    basement_surface: {
      label: t('Basement surface'),
      name: 'basement_surface',
      renderFn: sqrMetersSuffix,
    },
    weighted_floor_surface: {
      label: t('Weighted floor surface'),
      name: 'weighted_floor_surface',
      renderFn: sqrMetersSuffix,
    },
    building_volume: {
      label: t('Building volume'),
      name: 'building_volume',
      renderFn: (value: number | null | undefined) =>
        value != null ? `${value} m³` : null,
    },
    // UNITS
    number_of_residential_units: {
      label: t('numberOfResidentialUnits'),
      name: 'number_of_residential_units',
    },
    number_of_commercial_units: {
      label: t('numberOfCommercialUnits'),
      name: 'number_of_commercial_units',
    },
    // ROOMS
    number_of_rooms: {
      label: t('Number of rooms'),
      name: 'number_of_rooms',
    },
    number_of_bedrooms: {
      label: t('Number of bedrooms'),
      name: 'number_of_bedrooms',
    },
    number_of_bathrooms: {
      label: t('Number of bathrooms'),
      name: 'number_of_bathrooms',
    },
    number_of_toilets: {
      label: t('Number of toilets'),
      name: 'number_of_toilets',
    },
    // FLOORS
    number_of_floors: {
      label: t('numberOfFloors'),
      name: 'number_of_floors',
    },
    floor_of_flat: {
      label: t('floorOfFlat'),
      name: 'floor_of_flat',
    },
    number_of_lifts: {
      label: t('Number of elevators'),
      name: 'number_of_lifts',
    },
    side: {
      label: t('side'),
      name: 'side',
      renderFn: (value: string | null | undefined) =>
        getPropertySides(t).find(s => s.value === value)?.label ?? value,
    },
    orientation: {
      label: t('Orientation'),
      name: 'orientation',
      renderFn: (_, property) =>
        property != null
          ? extractPropertyOrientations({
              is_oriented_north: property.is_oriented_north ?? false,
              is_oriented_south: property.is_oriented_south ?? false,
              is_oriented_east: property.is_oriented_east ?? false,
              is_oriented_west: property.is_oriented_west ?? false,
            })
              .map(o => getOrientationTranslation(t, o))
              .join(', ')
          : null,
    },
    // PARKING
    number_of_indoor_parkings: {
      label: t('indoorParking'),
      name: 'number_of_indoor_parkings',
    },
    number_of_outdoor_parkings: {
      label: t('outdoorParking'),
      name: 'number_of_outdoor_parkings',
    },
    // HVAC
    heating: {
      label: t('Heating'),
      name: 'heating',
      renderFn: value => getPropertyHeatingLabel(value, t),
    },
    heating_distribution: {
      label: t('Heating distribution'),
      name: 'heating_distribution',
      renderFn: value => getPropertyHeatingDistributionLabel(value, t),
    },
    cooling: {
      label: t('Cooling'),
      name: 'cooling',
      renderFn: value => getPropertyCoolingLabel(value, t),
    },
    // ENERGY EFFICIENCY
    energy_consumption_class: {
      label: t('energyConsumptionClass'),
      name: 'energy_consumption_class',
      renderFn: (value: string | null | undefined) =>
        getEnergyClassValues(t).find(c => c.value === value)?.label ?? value,
    },
    gas_emissions_class: {
      label: t('gasEmissionsClass'),
      name: 'gas_emissions_class',
      renderFn: (value: string | null | undefined) =>
        getEnergyClassValues(t).find(c => c.value === value)?.label ?? value,
    },
    // CONSTRUCTION
    construction_year: {
      label: t('Construction year'),
      name: 'construction_year',
    },
    renovation_year: {
      label: t('Renovation year'),
      name: 'renovation_year',
    },
    building_standard: {
      label: t('buildingStandard'),
      name: 'building_standard',
      renderFn: (value: number | null | undefined) => getRatingLabel(value, t),
    },
    building_condition: {
      label: t('buildingCondition'),
      name: 'building_condition',
      renderFn: (value: number | null | undefined) => getRatingLabel(value, t),
    },
  };

  return Object.fromEntries(
    Object.entries(fields).map(([key, value]) => {
      const shouldRender = propertyDetailsConditionalFields.get(key as any);
      return [key, { ...value, shouldRender }];
    }),
  ) as TFieldsDefinition<keyof PropertyDetailsData>;
};

export const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  property,
}) => {
  const { t } = useLocale();
  const fields = getPropertyDetailsFieldsDefinition(t);

  return (
    <PropertyDataList
      fieldDefinition={fields}
      property={property}
      title={t('Property details')}
    />
  );
};
